// Safe JSON serialization that never throws.
//
// Some app state (e.g. restored_conversation_messages, or a visualisation's
// `data` object) can contain circular references or live DOM/React-fiber nodes
// — JSON.stringify_on those throws "Converting circular structure to JSON" and
// aborts_the_React_commit, white-screening the app. This replacer strips the
// offending values instead of crashing.
//
// Usage: replace `JSON.stringify(value)` with `safeStringify(value)` anywhere the
// input may come from previously-persisted or user-derived state.

type Replacer = (this: unknown, key: string, value: unknown) => unknown;

export function safeStringify(value: unknown): string {
  if (typeof value === "string") return value;

  const seen = new WeakSet<object>();
  const domAvailable = typeof Node !== "undefined";

  const replacer: Replacer = (_key, val) => {
    if (val === null || val === undefined) return val;
    const t = typeof val;
    if (t === "function" || t === "symbol" || t === "bigint") return undefined;
    if (t === "object") {
      // Live DOM nodes / React fibers must not be serialized.
      if (domAvailable && val instanceof Node) return "[DOMNode]";
      if (typeof File !== "undefined" && val instanceof File) {
        return { __file: val.name, size: val.size, type: val.type };
      }
      if (typeof Blob !== "undefined" && val instanceof Blob) {
        return { __blob: val.size, type: val.type };
      }
      if (seen.has(val as object)) return "[Circular]";
      seen.add(val as object);
    }
    return val;
  };

  try {
    return JSON.stringify(value, replacer);
  } catch {
    // Last-resort fallback: return an empty payload rather than throwing.
    return "{}";
  }
}

/** Parse_a_value_previously_written_with_safeStringify, returning `fallback` on any error. */
export function safeParse<T>(text: string | null | undefined, fallback: T): T {
  if (!text) return fallback;
  try {
    return JSON.parse(text) as T;
  } catch {
    return fallback;
  }
}
